import { domain } from 'modules/data';
import { useEffect, useMemo } from 'react';
import { CheckAmaxPayPaymentDetailsDiscrepancies } from 'shared/data/webApp/v1';

import { useXeroAmaxPayBatchPaymentFormContext } from '../../XeroAmaxPayBatchPaymentForm.context';
import { createEmptySupplierWithItems, updateSupplierWithItems } from './BillsInformation.helpers';
import { SupplierWithItems } from './BillsInformation.types';

export const useMergedBySupplierItems = (
    items: domain.AmaxPayPaymentItem[],
    paymentDetailsDiscrepanciesOnChecking: CheckAmaxPayPaymentDetailsDiscrepancies['response']['items'] | null
) => {
    const { sortedSuppliersWithItems: mergedBySupplierItems, itemsCanBeMerged } = useMemo(() => {
        const paymentDetailsWithVersions: Record<string, string> = {};

        let itemsHasDuplicatesContactAndPaymentDetails = false;
        let paymentDetailsHasConflictingVersions = false;
        let itemsHasMissingData = false;

        const suppliersWithItems: Record<string, SupplierWithItems> = {};

        items.forEach((item) => {
            if (!item.contact?.id || !item.paymentDetails) {
                itemsHasMissingData = true;

                return;
            }

            const key = `${item.contact.id},${item.paymentDetails.id}`;

            if (paymentDetailsWithVersions[item.paymentDetails.id]) {
                if (paymentDetailsWithVersions[item.paymentDetails.id] !== item.paymentDetails.versionId) {
                    paymentDetailsHasConflictingVersions = true;
                }
            } else {
                paymentDetailsWithVersions[item.paymentDetails.id] = item.paymentDetails.versionId;
            }

            if (!suppliersWithItems[key]) {
                suppliersWithItems[key] = createEmptySupplierWithItems();
            } else {
                itemsHasDuplicatesContactAndPaymentDetails = true;
            }

            updateSupplierWithItems(suppliersWithItems[key], item, paymentDetailsDiscrepanciesOnChecking);
        });

        const sortedSuppliersWithItems = Object.values(suppliersWithItems).sort((a, b) => {
            return (
                a.total.contact.text.localeCompare(b.total.contact.text) ||
                a.total.paymentDetailsName.localeCompare(b.total.paymentDetailsName)
            );
        });

        const itemsCanBeMerged =
            !itemsHasMissingData && itemsHasDuplicatesContactAndPaymentDetails && !paymentDetailsHasConflictingVersions;

        return { sortedSuppliersWithItems, itemsCanBeMerged };
    }, [items, paymentDetailsDiscrepanciesOnChecking]);

    return { mergedBySupplierItems, itemsCanBeMerged };
};

export const useMergedBySupplierListener = (itemsCanBeMerged: boolean) => {
    const {
        billBatchPayment: { isMergedBySupplier },
        changeIsMergedBySupplier,
    } = useXeroAmaxPayBatchPaymentFormContext();

    useEffect(() => {
        if (isMergedBySupplier && !itemsCanBeMerged) {
            changeIsMergedBySupplier(false);
        }
    }, [isMergedBySupplier, itemsCanBeMerged, changeIsMergedBySupplier]);
};
